/**
 * Bridge between a DLR's request items and RealmSchemaEditor's
 * {verbCaps, scopeFilters} shape.
 *
 * The editor governs SCOPE — which verbs, over which schemas, with which filters.
 * It has no UI for the descriptive fields of the DLR request-item shape a developer
 * authors via CRUX.md §9: `key`, `label`, `description`, `min`, `max`, `required`, `tier`.
 *
 * Those fields must survive a round-trip through the editor. Rebuilding items from
 * REQUEST_TYPE_REGISTRY defaults (which is what `migrateDlrVerbsToInputs` does on its
 * own) silently discards them, so the output adapter merges onto the prior items and
 * only replaces the parts the editor actually owns.
 *
 * The editor's model is also COARSER than the DLR's: one verb has one schema filter,
 * whereas a DLR may carry several items for the same verb (`read_goals` on [goal],
 * `read_journal` on [journal] — each its own row on the consent screen, with its own
 * key, label and tier). Loading unions those into the one filter the editor can show,
 * and writing passes every verb the editor did not touch back through verbatim, so the
 * collapse only happens to a verb whose scope the developer actually changed here.
 *
 * Previously duplicated — and diverged — in MyAppsTab.js and DeveloperDashboard.js.
 */
import {
  REQUEST_TYPE_REGISTRY,
  deriveVerbsFromRequests,
  migrateDlrVerbsToInputs,
  requestTypeOf,
} from '../constants/formA';

/**
 * Every item field the editor owns. On save these are cleared and re-derived from
 * editor state, so that clearing a restriction in the UI actually clears it. Any
 * field NOT in this list belongs to the developer and is carried across untouched.
 */
const EDITOR_OWNED_FIELDS = [
  'schema_ids', 'source',
  'relationship_types', 'rel_filter',
  'categories', 'actor_types',
  'plugin_ids',
  // Owned so that CLEARING a per-tool limit in the editor actually clears it.
  // Left out, a restriction removed in the UI would survive the save, and the
  // request would keep asking under a narrowing its author had just deleted.
  'tools',
  'friends', 'groups', 'realms',
  'visibility', 'assignable_tag_ids',
];

const DISCOVERY_VERBS = ['friends', 'groups', 'realms'];

function pickEditorOwned(item) {
  const out = {};
  for (const f of EDITOR_OWNED_FIELDS) {
    if (item[f] !== undefined) out[f] = item[f];
  }
  return out;
}

/** Group items by canonical request type, in document order. */
function groupByType(items) {
  const groups = new Map();
  for (const item of (items || [])) {
    const rt = requestTypeOf(item);
    if (!rt) continue;
    if (!groups.has(rt)) groups.set(rt, []);
    groups.get(rt).push(item);
  }
  return groups;
}

/**
 * The one schema filter the editor can show for a set of same-verb items.
 *
 * The union, because that is the access the items add up to. An item with no
 * schema restriction is unrestricted, and unrestricted ∪ anything is unrestricted —
 * so one such item makes the whole verb global (returns null).
 */
function unionSchemaIds(items) {
  const ids = [];
  for (const item of items) {
    if (!item.schema_ids?.length) return null;
    for (const id of item.schema_ids) if (!ids.includes(id)) ids.push(id);
  }
  return ids.length ? ids : null;
}

/** DLR input items → {verbCaps, scopeFilters} for RealmSchemaEditor. */
export function dlrInputsToEditorState(dlrInputs) {
  const verbCaps = deriveVerbsFromRequests(dlrInputs || []);
  const scopeFilters = {};
  for (const [rt, items] of groupByType(dlrInputs)) {
    const scopeKey = REQUEST_TYPE_REGISTRY[rt]?.scopeFilterKey;
    if (!scopeKey) continue;
    // The DLR is schema-centric: tag_ids and node_ids are the user's to choose at
    // consent time, so a developer never sets them here.
    const ids = unionSchemaIds(items);
    if (ids) scopeFilters[scopeKey] = { schema_ids: ids };
  }
  return { verbCaps, scopeFilters };
}

const sortedIds = ids => JSON.stringify([...(ids || [])].map(String).sort());

/**
 * Did the editor leave this verb's scope as it was loaded?
 *
 * Compared on the schema filter only, because that is all the load side puts in
 * front of the editor: node_ids are never authored in a DLR, and the activity /
 * plugin filters are not loaded, so the editor cannot have changed them.
 */
function scopeUnchanged(rt, scopeFilters, loaded) {
  const scopeKey = REQUEST_TYPE_REGISTRY[rt]?.scopeFilterKey;
  if (!scopeKey) return true;
  const now = scopeFilters?.[scopeKey]?.schema_ids;
  const was = loaded.scopeFilters?.[scopeKey]?.schema_ids;
  if (!now?.length && !was?.length) return true;
  return sortedIds(now) === sortedIds(was);
}

/**
 * RealmSchemaEditor output → dlr_inputs, preserving developer-authored fields.
 *
 * @param {object} verbCaps      editor verb capabilities
 * @param {object} scopeFilters  editor scope filters
 * @param {Array}  priorInputs   the DLR items being edited; omit when registering
 *                               a new app, where there is nothing to preserve.
 */
export function editorStateToDlrInputs(verbCaps, scopeFilters, priorInputs = []) {
  const prior = groupByType(priorInputs);
  const loaded = dlrInputsToEditorState(priorInputs);

  // One item per currently-granted request type. A type the editor no longer grants
  // is absent here, which is how a revocation is expressed.
  const granted = migrateDlrVerbsToInputs(verbCaps);

  return granted.flatMap((fresh) => {
    const rt = requestTypeOf(fresh);
    const previous = prior.get(rt) || [];

    // A verb the editor did not touch keeps every item it had, verbatim. This is
    // what lets several same-verb items — and scope the editor never loaded, like
    // plugin_ids — survive an edit made to some other verb.
    if (previous.length && scopeUnchanged(rt, scopeFilters, loaded)) {
      if (rt !== 'discovery') return previous;
      const wasVerbs = new Set(loaded.verbCaps.discovery || []);
      const nowVerbs = new Set(verbCaps.discovery || []);
      if (wasVerbs.size === nowVerbs.size && [...nowVerbs].every(v => wasVerbs.has(v))) return previous;
    }

    // Keep the developer's fields; let the editor own scope entirely. Several
    // prior items fold into one here — the editor has one filter per verb, so a
    // changed scope can only be expressed as one item.
    const out = { ...(previous[0] || fresh) };
    for (const f of EDITOR_OWNED_FIELDS) delete out[f];
    Object.assign(out, pickEditorOwned(fresh));

    // Discovery is stored one canonical item per verb, not as flags. Emitting the
    // flag dialect onto an item that already carries `verb: realms` would keep
    // realms requested however the flags were set, because the server unions the
    // two spellings.
    if (rt === 'discovery') {
      const base = { ...out };
      delete base.verb;
      for (const f of DISCOVERY_VERBS) delete base[f];
      const asked = DISCOVERY_VERBS.filter(v => (verbCaps.discovery || []).includes(v));
      return asked.map(v => ({ ...base, verb: v }));
    }

    const scopeKey = REQUEST_TYPE_REGISTRY[rt]?.scopeFilterKey;
    const sf = scopeKey ? scopeFilters?.[scopeKey] : null;

    if (!sf) {
      // No scope filter means the editor granted this verb globally. The registry
      // default is `source: 'schema'` with an empty `schema_ids`, which reads as
      // "restricted to nothing" downstream — `toCanonicalDlrItem` would emit
      // `schemas: []`. Say unrestricted explicitly instead.
      if (Array.isArray(out.schema_ids) && out.schema_ids.length === 0) {
        delete out.schema_ids;
        out.source = 'any';
      }
      return [out];
    }

    if (sf.schema_ids !== undefined) {
      out.schema_ids = sf.schema_ids;
      out.source = sf.schema_ids.length > 0 ? 'schema' : 'any';
    }
    if (sf.relationship_types !== undefined) out.relationship_types = sf.relationship_types;
    if (sf.filter !== undefined) out.rel_filter = sf.filter;
    if (sf.categories !== undefined) out.categories = sf.categories;
    if (sf.actor_types !== undefined) out.actor_types = sf.actor_types;
    if (sf.plugin_ids !== undefined) out.plugin_ids = sf.plugin_ids;
    if (sf.friends !== undefined) out.friends = sf.friends;
    if (sf.groups !== undefined) out.groups = sf.groups;
    if (sf.realms !== undefined) out.realms = sf.realms;
    if (sf.visible_tag_ids !== undefined) {
      out.visibility = sf.visible_tag_ids.length > 0 ? 'some' : 'all';
    }
    if (sf.assignable_tag_ids !== undefined) out.assignable_tag_ids = sf.assignable_tag_ids;
    return [out];
  });
}

/**
 * Per-tool modes a DLR already asks for: { pluginId: { toolId: mode } }.
 *
 * A plugin is not a capability — Google is calendar, contacts, mail, files and
 * watch history — so a developer who needs only the calendar should be able to
 * ASK for only the calendar. Before this the access page could not express it,
 * which meant every app requested the widest possible thing and people were
 * asked to approve it.
 *
 * Folded across items with the NARROWER mode winning, the same ranking
 * authz/dlr.py and authz/access.py use. Two layers that ranked these
 * differently would make a request mean different things depending on which
 * one read it.
 */
export function dlrInputsToToolModes(dlrInputs) {
  const ORDER = { deny: 0, ask: 1, allow: 2 };
  const out = {};
  for (const item of dlrInputs || []) {
    if (item?.resource !== 'plugins' || !item.tools || typeof item.tools !== 'object') continue;
    for (const [pid, modes] of Object.entries(item.tools)) {
      if (!modes || typeof modes !== 'object') continue;
      const cur = out[String(pid)] || (out[String(pid)] = {});
      for (const [tid, mode] of Object.entries(modes)) {
        if (!(mode in ORDER)) continue;
        const prev = cur[String(tid)];
        if (prev === undefined || ORDER[mode] < ORDER[prev]) cur[String(tid)] = mode;
      }
    }
  }
  return out;
}

/**
 * Write tool modes back onto the plugins request item.
 *
 * Only NARROWINGS are stored. An all-allow map says nothing `plugin_ids` did
 * not, and persisting it would freeze the tool list: a tool the plugin
 * publishes tomorrow would read as refused by a request whose author meant "the
 * whole plugin". The same rule the realm editor follows, for the same reason.
 *
 * Leaves every other item untouched, and leaves the developer's own fields on
 * the plugins item alone — this owns `tools` and nothing else.
 */
export function applyToolModesToDlrInputs(dlrInputs, toolModes) {
  const narrowing = {};
  for (const [pid, modes] of Object.entries(toolModes || {})) {
    if (!modes || typeof modes !== 'object') continue;
    const kept = {};
    for (const [tid, mode] of Object.entries(modes)) {
      if (mode === 'ask' || mode === 'deny') kept[tid] = mode;
    }
    if (Object.keys(kept).length) {
      for (const [tid, mode] of Object.entries(modes)) {
        if (mode === 'allow') kept[tid] = mode;
      }
      narrowing[String(pid)] = kept;
    }
  }
  const has = Object.keys(narrowing).length > 0;
  let touched = false;
  const next = (dlrInputs || []).map((item) => {
    if (item?.resource !== 'plugins') return item;
    touched = true;
    const copy = { ...item };
    if (has) copy.tools = narrowing;
    else delete copy.tools;
    return copy;
  });
  // Nothing to hang it on: a request that does not ask for plugins cannot carry
  // a tool restriction, and inventing an item here would silently add a
  // capability the developer never requested.
  return touched ? next : (dlrInputs || []);
}
